import { property } from '@wonderlandengine/api/decorators.js';
import { Component, MeshComponent, Object3D, } from '@wonderlandengine/api';
import { CloudData } from '../data/cloud-data.js';
import { Configuration, SpareWheelOptions, WheelTypes } from '../data/configuration.js';
import { WheelDescription } from './components/wheel-description.js';
import { PaintDescription } from './components/paint-description.js';
import { CarPbrMaterial } from './paint-configurator.js';
import { Utilities } from '../utilities/utilities.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TweenManager } from '../utilities/tween-manager.js';

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const COLOUR_TWEEN_TIME_IN_MS = 250;

/**
 * Wheel Configurator:
 */
export class WheelConfigurator extends Component {
	static TypeName = 'wheel-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	wheelDescriptions!: Object3D;

	@property.object({ required: true })
	wheelNutDescriptions!: Object3D;

	@property.object({ required: true })
	alloyWheel!: Object3D;

	@property.object({ required: true })
	wireWheel!: Object3D;

	@property.object({ required: true })
	wheelNuts!: Object3D;

	@property.object({ required: true })
	spareWheelBase!: Object3D;

	@property.object({ required: true })
	spareWheelAlloy!: Object3D;

	@property.object({ required: true })
	spareWheelWire!: Object3D;

	@property.object({ required: true })
	spareWheelCover!: Object3D;

	@property.object({ required: true })
	spareWheelNut!: Object3D;

	@property.object({ required: true })
	cobraStopLamp!: Object3D;

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _wheelDescriptions!: WheelDescription[];
	private _wheelNutDescriptions!: PaintDescription[];

	private _alloyWheel!: MeshComponent;
	private _wireWheel!: MeshComponent;
	private _wheelNuts!: MeshComponent;
	private _spareWheelAlloy!: MeshComponent;
	private _spareWheelWire!: MeshComponent;
	private _spareWheelNut!: MeshComponent;
	
	private _wasWireWheel: boolean | null = null;
	private _tweenManager = new TweenManager();

	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start() {

		// get components.
		this._wheelDescriptions = this.wheelDescriptions.getComponents(WheelDescription)!;
		this._wheelNutDescriptions = this.wheelNutDescriptions.getComponents(PaintDescription)!;

		this._alloyWheel = this.alloyWheel.getComponent(MeshComponent)!;
		this._wireWheel = this.wireWheel.getComponent(MeshComponent)!;
		this._wheelNuts = this.wheelNuts.getComponent(MeshComponent)!;
		this._spareWheelAlloy = this.spareWheelAlloy.getComponent(MeshComponent)!;
		this._spareWheelWire = this.spareWheelWire.getComponent(MeshComponent)!;
		this._spareWheelNut = this.spareWheelNut.getComponent(MeshComponent)!;
	}

	update() {
		this._tweenManager.update();
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/**
	 * Selects a wheel style with the given ID, updating the relevant meshes/materials on the vehicle model.
	 */
	selectWheelStyle(id: string) {

		// get the wheel description for the given ID.
		const wheelDescription = this._wheelDescriptions.find(element => element.wheelId === id);
		if (!wheelDescription) {
			console.error('No wheel description found for id: ', id);
			return;
		}
		
		// get the relevant material.
		const material = wheelDescription.paintMaterial as CarPbrMaterial;
		if (!material) {
			console.error('No paint material assigned for id: ', id);
			return;
		}

		// get the relevant mesh components.
		const wheelMesh = wheelDescription.isWireWheel ? this._wireWheel : this._alloyWheel;
		const spareWheelMesh = wheelDescription.isWireWheel ? this._spareWheelWire : this._spareWheelAlloy;

		// if the wheel type (alloy or wire) has changed (or on first call)...
		if (wheelDescription.isWireWheel !== this._wasWireWheel) {

			// activate the relevant objects.
			Utilities.setActiveAll(this.alloyWheel, !wheelDescription.isWireWheel);
			Utilities.setActiveAll(this.wireWheel, wheelDescription.isWireWheel);

			// swap the spare wheel to match, but only when one is fitted - otherwise both spare meshes must stay hidden.
			if (Configuration.spareWheelOption !== SpareWheelOptions.None) {
				Utilities.setActiveAll(this.spareWheelWire, wheelDescription.isWireWheel);
				Utilities.setActiveAll(this.spareWheelAlloy, !wheelDescription.isWireWheel);
			}

			// set the materials.
			wheelMesh.material = material;
			spareWheelMesh.material = material;
		}

		// otherwise, smoothly fade between the current material and the selected material.
		else {
			this._fadeColour(wheelMesh, material);
			this._fadeColour(spareWheelMesh, material);
		}

		// update the flag.
		this._wasWireWheel = wheelDescription.isWireWheel;
	}

	/**
	 * Selects the 'spare wheel' or 'no spare wheel option', enabling or disabling the relevant meshes on the vehicle model.
	 */
	selectSpareWheelOption(id: string) {

		// no spare.
		if (id === SpareWheelOptions.None) {
			this.spareWheelCover.active = true;
			this.spareWheelBase.active = false;
			Utilities.setActiveAll(this.cobraStopLamp, false);
			Utilities.setActiveAll(this.spareWheelWire, false);
			Utilities.setActiveAll(this.spareWheelAlloy, false);
		}

		// spare.
		else {
			const isWire = CloudData.getWheelTypeForId(Configuration.wheelStyle) === WheelTypes.Wire;
			this.spareWheelCover.active = false;
			this.spareWheelBase.active = true;
			Utilities.setActiveAll(this.spareWheelWire, isWire);
			Utilities.setActiveAll(this.spareWheelAlloy, !isWire);
			if (Configuration.isUsRegion()) {
				Utilities.setActiveAll(this.cobraStopLamp, false);
			} else {
				Utilities.setActiveAll(this.cobraStopLamp, true);
			}
		}
	}

	/**
	 * Selects the wheel-nut option with the given ID, updating the relevant meshes/materials on the vehicle model.
	 */
	selectWheelNutOption(id: string) {

		// get the wheel-nut description for the given ID.
		const wheelNutDescription = this._wheelNutDescriptions.find(element => element.paintId === id);
		if (!wheelNutDescription) {
			console.error('No wheel nut description found for id: ', id);
			return;
		}

		// get the relevant material, which will be null if the option for no wheel nuts is chosen.
		const material = wheelNutDescription.paintMaterial as CarPbrMaterial;

		// get the relevant mesh components.
		const wheelNutsMesh =  this._wheelNuts;
		const spareWheelNutMesh = this._spareWheelNut;

		// set the materials, unless the option for no wheel-nuts is chosen.
		// n.b. this only happens when alloy wheels are selected, so the relevant alloy wheel meshes are shown, hiding the wheel-nut meshes.
		if (material) {
			wheelNutsMesh.material = material;
			spareWheelNutMesh.material = material;
		}
	}

	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------

	/**
	 * Smoothly fades the colour of the material on the given mesh between the current colour and the colour defined by the given material,
	 * using a tween animation to change the relevant material properties.
	 */
	private _fadeColour(mesh: MeshComponent, targetMaterial: CarPbrMaterial) {

		// ensure the given mesh has a material assigned to it.
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		// clone the current material (as we don't want to change the source material itself).
		const material = mesh.material.clone() as CarPbrMaterial;
		mesh.material = material;
		
		// tween the relevant material properties to change the colour.
		const colourTween = new Tween({
			albedoColor: material.albedoColor,
			metallicFactor: material.metallicFactor,
			smoothnessFactor: material.smoothnessFactor
		});
		colourTween
			.to(
				{
					albedoColor: targetMaterial.albedoColor,
					metallicFactor: targetMaterial.metallicFactor,
					smoothnessFactor: targetMaterial.smoothnessFactor
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				material.albedoColor = c.albedoColor;
				material.metallicFactor = c.metallicFactor;
				material.smoothnessFactor = c.smoothnessFactor;
			})
			.easing(Easing.Quadratic.InOut);
		this._tweenManager.start(colourTween);
	}
}
